import type { AuthProvider } from '@words-happen/ui-components';
import {
  ChevronLeftIcon,
  SignInPrompt,
  SOCIAL_AUTH_PROVIDERS,
  SocialAuthRow,
  WordsHappenButton,
  WordsHappenDivider,
  WordsHappenFormAlert,
  WordsHappenPasswordField,
  WordsHappenTextField,
} from '@words-happen/ui-components';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useValidatedForm } from '../auth-form';
import { ScreenFrame } from '../screen-frame';
import styles from './sign-in-screen.module.scss';
import { type SignInCredentials, validateEmail, validatePassword } from './sign-in-validation';

export interface SignInScreenProps {
  /** Called with the trimmed email and the password once both are valid. */
  onSubmit: (credentials: SignInCredentials) => void;
  /** A sign-in is in flight: the submit button shows its spinner and is disabled. */
  submitting?: boolean;
  /** A form-level error, e.g. from the server. Shown in an alert above the submit button. */
  error?: React.ReactNode;
  /** A confirmation under the subhead, e.g. that the password was changed. Shown in a role="status" line. */
  notice?: React.ReactNode;
  /**
   * Prefills the email when the screen mounts, e.g. the address the player just registered or signed in with; the
   * password then gets focus.
   */
  defaultEmail?: string;
  /** The social providers offered above the form. Email is left out: this is the email route. */
  authProviders?: readonly AuthProvider[];
  /** The providers that can be used; the rest are shown disabled, "coming soon". Every provider when left out. */
  enabledProviders?: readonly AuthProvider[];
  /** A provider whose sign-in is starting: its button shows a spinner and the others are disabled. */
  busyProvider?: AuthProvider;
  onSelectProvider: (provider: AuthProvider) => void;
  /** Called with the email field's trimmed value, which may be empty or invalid, to prefill the reset request. */
  onForgotPassword: (email: string) => void;
  /**
   * "Create an account": from the footer with no email, or from the registerOffer with the email field's trimmed
   * value, which may be empty.
   */
  onRegister: (email?: string) => void;
  /**
   * Offers, under the error, to create an account with the email typed, e.g. after a failed sign-in. It never says
   * whether the address has an account. The footer's "Create an account" is hidden while it shows.
   */
  registerOffer?: boolean;
  onBack: () => void;
  className?: string;
}

const VALIDATORS = { email: validateEmail, password: validatePassword };

/**
 * Email and password sign-in, with the social providers as a shortcut. Presentational only: submitting,
 * errors from the server and every navigation are the caller's.
 */
export const SignInScreen: React.FC<SignInScreenProps> = ({
  onSubmit,
  submitting = false,
  error,
  notice,
  defaultEmail = '',
  authProviders = SOCIAL_AUTH_PROVIDERS,
  enabledProviders,
  busyProvider,
  onSelectProvider,
  onForgotPassword,
  onRegister,
  registerOffer = false,
  onBack,
  className,
}) => {
  const { values, errors, change, blur, validateAll } = useValidatedForm(
    { email: defaultEmail, password: '' },
    VALIDATORS,
  );
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: on mount only, like the prefilled value itself
  useEffect(() => {
    if (defaultEmail) passwordRef.current?.focus();
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const invalid = validateAll();
    if (invalid) {
      (invalid === 'email' ? emailRef : passwordRef).current?.focus();
      return;
    }

    onSubmit({ email: values.email.trim(), password: values.password });
  };

  return (
    <ScreenFrame className={className}>
      <header className={styles.topBar}>
        <WordsHappenButton
          size="sm"
          className={styles.back}
          aria-label="Back"
          icon={<ChevronLeftIcon size={22} />}
          onClick={onBack}
        />
      </header>

      <main className={styles.main}>
        <h1 className={styles.heading}>Welcome back</h1>
        <p className={styles.subhead}>Sign in to pick up your streak, scores and games.</p>
        {notice ? (
          <p role="status" className={styles.notice}>
            {notice}
          </p>
        ) : null}

        <SocialAuthRow
          compact
          className={styles.providers}
          providers={authProviders}
          enabledProviders={enabledProviders}
          busyProvider={busyProvider}
          onSelect={onSelectProvider}
        />
        <WordsHappenDivider label="or" className={styles.divider} />

        <form className={styles.form} aria-label="Sign in with email" noValidate onSubmit={handleSubmit}>
          <WordsHappenTextField
            ref={emailRef}
            label="Email"
            name="email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={values.email}
            error={errors.email}
            onChange={change('email')}
            onBlur={blur('email')}
          />
          <WordsHappenPasswordField
            ref={passwordRef}
            label="Password"
            name="password"
            autoComplete="current-password"
            value={values.password}
            error={errors.password}
            onChange={change('password')}
            onBlur={blur('password')}
          />
          <SignInPrompt
            className={styles.forgot}
            prefix={null}
            action="Forgot password?"
            onAction={() => onForgotPassword(values.email.trim())}
          />

          {error ? <WordsHappenFormAlert>{error}</WordsHappenFormAlert> : null}
          {registerOffer ? (
            <SignInPrompt
              className={styles.registerOffer}
              prefix="New here?"
              action="Create an account with this email"
              onAction={() => onRegister(values.email.trim())}
            />
          ) : null}

          <WordsHappenButton type="submit" variant="coral" size="lg" loading={submitting}>
            Sign in
          </WordsHappenButton>
        </form>

        {registerOffer ? null : (
          <SignInPrompt
            className={styles.register}
            prefix="New here?"
            action="Create an account"
            onAction={() => onRegister()}
          />
        )}
      </main>
    </ScreenFrame>
  );
};
